import { useEffect, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { useAuthorize } from '~features/authentication';
import { RevenuesService } from '~features/finances';

import { useStyles } from './style';
import { Table, Label } from '../../../../../components';
import { isObject, decimalAmount, getFinanceTableHeaders } from '../../../../../shared/utility';
import * as actions from '../../../../../store/actions';

const localizeTime = (value) => {
	const date = new Date(value);

	return `${date.getUTCDate()}-${date.getUTCMonth() + 1}-${date.getUTCFullYear()}`;
};

interface FinanceCurrentProps {
	financeType?: string;
	date?: string;
	title?: string;
	name?: string;
	id?: string;
	currentFinanceEvents?: object;
	onFetchCurrentRevenue?(...args: unknown[]): unknown;
	onFetchPersonalRevenue?(...args: unknown[]): unknown;
	personalRevenue?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	setMonthlyTotal?(...args: unknown[]): unknown;
}

const service = new RevenuesService();

const FinanceCurrent = (props: FinanceCurrentProps) => {
	const { date, title, name, id, onFetchPersonalRevenue, personalRevenue, setMonthlyTotal } = props;
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuthorize();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();

	const { data: personalRevenueData, isLoading: personalRevenueLoading } = useSWR(
		[
			service.basePath,
			date,
			service.personalSubPath,
			{
				organisationId: isSuperAdmin() ? id : undefined,
				excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
			},
		],
		([_1, dateSubPath, _2, args]) => service.getPersonalRevenuesUnmapped(dateSubPath, args)
	);

	useEffect(() => {
		if (isObject(personalRevenueData) && !personalRevenueLoading) {
			setMonthlyTotal(personalRevenueData.amount.subtotal - personalRevenueData.amount.discount);
		}
	}, [personalRevenueLoading]);

	const tableHeader = getFinanceTableHeaders();

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const tableBody =
		isObject(personalRevenueData) && !personalRevenueLoading ?
			personalRevenueData.billings.map((item) => [
				{
					content: (
						<Box className={classes.cell}>
							<Link
								color='inherit'
								component={RouterLink}
								to={`/financeManagement/revenue/${item.start}/${title}/partner/${id}/${encodeURIComponent(name ?? '')}/parsonal`}
							>
								{`${localizeTime(item.start)} ${t('ui.till')} ${localizeTime(item.end)}`}
							</Link>
						</Box>
					),
				},
				{ content: decimalAmount(item.amount.subtotal, item.amount) },
				{ content: decimalAmount(item.amount.discount, item.amount) },
				{ content: decimalAmount(item.amount.subtotal - item.amount.discount, item.amount) },
				{ content: decimalAmount(item.amount.priceVat, item.amount) },
				{ content: decimalAmount(item.amount.refund, item.amount) },
				{ content: decimalAmount(item.amount.totalPrice, item.amount) },
				{ content: decimalAmount(item.open, item.amount) },
				{
					content: (
						<Box>
							<Label type={statusColor[item.status]}>{statusText[item.status]}</Label>
						</Box>
					),
				},
			])
		:	Array(2)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	const titleWithInformation =
		isObject(personalRevenueData) ?
			{
				header: t('ui.label.personalFinance'),
				sideHeader: decimalAmount(
					personalRevenueData.amount.subtotal - personalRevenueData.amount.discount,
					personalRevenueData.amount
				),
			}
		:	null;

	return (
		<div className={classes.root}>
			<Table
				body={tableBody}
				cellStyle={classes.cellStyle}
				header={tableHeader}
				headerLabel={t('ui.label.finance.totaExclVat')}
				isNotPaginate={true}
				loading={personalRevenueLoading}
				titleWithInformation={titleWithInformation}
				withoutPages={true}
			/>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		personalRevenue: state.details.personalRevenue,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchPersonalRevenue: (date, id, excludeOneOffPayments?: boolean) =>
			dispatch(actions.fetchPersonalRevenue(date, id, excludeOneOffPayments)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(FinanceCurrent);
